import { assertNever, definedMap } from "@glideapps/ts-necessities";
import {
    AppDescription,
    AppFeatures,
    AppKind,
    defaultAppDescriptionText,
    defaultNonUserAppFeatures,
    isolateNonUserAppFeatures,
    makeSourceMetadataForAppDescription,
    SourceMetadata,
} from "../components/SerializedApp";
import { AppSettings } from "../firebase-function-types";
import { WireComponentKind } from "../wire/component-kind";
import { NonUndefined } from "utility-types";
import { CardCollectionComponentDescription, HeroSectionDescription } from "./components/fluent-components";
import { ComponentKindInlineList } from "./components/inline-list";
import {
    makeActionProperty,
    makeEnumProperty,
    makeScreenProperty,
    makeSwitchProperty,
    makeTableProperty,
} from "./components/property-utils";
import {
    ActionKind,
    addClassScreenName,
    ArrayScreenFormat,
    ClassScreenDescription,
    classScreenName,
    ComponentDescription,
    editClassScreenName,
    freeScreenPrefix,
    getTableName,
    makeEmptyComponentDescription,
    makeTableRef,
    ScreenDescription,
    ScreenDescriptionKind,
    sheetNameForTable,
    TabDescription,
    TableGlideType,
    TypeSchema,
} from "./description";
import { summaryColumnsForTable } from "./description-utils";
import {
    makeAddOrEditScreenDescriptionForTable,
    makeClassScreenDescription,
    makeComponentsForTable,
} from "./make-screen";
import { MAX_TABS } from "./screens";
import { getRandomIcon } from "../remote-icon-type";
import { ComponentConfiguratorContext, getAppKindFromAppDescriptionContext } from "./components";

type Summary = NonUndefined<ReturnType<typeof summaryColumnsForTable>>;

function makeSummary(table: TableGlideType): Summary | undefined {
    return summaryColumnsForTable(table, false, false, true, false);
}

function makeHero(summary: Summary): HeroSectionDescription {
    return {
        ...makeEmptyComponentDescription(WireComponentKind.Hero),
        title: summary.titleProperty,
        subtitle: summary.subtitleProperty,
        image: summary.imageURLProperty,
    };
}

function makeClassScreen(
    table: TableGlideType,
    fetchesData: boolean,
    components: readonly ComponentDescription[]
): ClassScreenDescription {
    return {
        kind: ScreenDescriptionKind.Class,
        type: makeTableRef(table),
        canEdit: false,
        canEditFilters: [],
        canDelete: false,
        canDeleteFilters: [],
        searchPlaceholder: undefined,
        isForm: false,
        fetchesData,
        title: undefined,
        components,
        transforms: [],
    };
}

function makeDefaultComponentsForPage(table: TableGlideType): readonly ComponentDescription[] {
    // FIXME: Do something better than just making a Hero here.
    const summary = makeSummary(table);
    return summary !== undefined ? [makeHero(summary)] : [];
}

export function makeDefaultComponents(
    ccc: ComponentConfiguratorContext,
    table: TableGlideType
): readonly ComponentDescription[] {
    const appKind = getAppKindFromAppDescriptionContext(ccc);
    switch (appKind) {
        case AppKind.App:
            return makeComponentsForTable(ccc, table);
        case AppKind.Page:
            return makeDefaultComponentsForPage(table);
        default:
            return assertNever(appKind);
    }
}

function makeDefaultClassScreenForPage(table: TableGlideType, fetchesData: boolean): ClassScreenDescription {
    return makeClassScreen(table, fetchesData, makeDefaultComponentsForPage(table));
}

export function makeDefaultClassScreen(
    table: TableGlideType,
    ccc: ComponentConfiguratorContext,
    fetchesData: boolean
): ClassScreenDescription {
    const appKind = getAppKindFromAppDescriptionContext(ccc);
    switch (appKind) {
        case AppKind.App:
            return makeClassScreenDescription(table, ccc, fetchesData);
        case AppKind.Page:
            return makeDefaultClassScreenForPage(table, fetchesData);
        default:
            return assertNever(appKind);
    }
}

export function makePageFromSchema(
    schema: TypeSchema,
    sourceMetadataArray: readonly SourceMetadata[],
    settings: AppSettings,
    features: AppFeatures
): AppDescription {
    const tables = schema.tables.filter(t => !getTableName(t).isSpecial);
    const tablesForTabs = tables.slice(0, MAX_TABS);
    const screenDescriptions: Record<string, ScreenDescription> = {};
    const tabs: TabDescription[] = [];
    let freeScreenIndex = 0;

    for (const table of tablesForTabs) {
        const components: ComponentDescription[] = [];

        const summary = makeSummary(table);

        if (summary !== undefined) {
            if (table.numDataRows === 1) {
                components.push(makeHero(summary));
            } else {
                const cardCollection: CardCollectionComponentDescription = {
                    ...makeEmptyComponentDescription(ComponentKindInlineList),
                    propertyName: makeTableProperty(getTableName(table)),
                    format: makeEnumProperty(ArrayScreenFormat.CardCollection),
                    title: summary.titleProperty,
                    subtitle: summary.subtitleProperty,
                    image: summary.imageURLProperty,
                    action: makeActionProperty({ kind: ActionKind.PushDetailScreen }),
                    caption: undefined,
                    allowSearch: makeSwitchProperty(true),
                    reverse: undefined,
                    limit: undefined,
                    groupByColumn: undefined,
                    components: undefined,
                    transforms: undefined,
                    actions: undefined,
                };
                components.push(cardCollection);
            }
        }

        const screenName = `${freeScreenPrefix}${freeScreenIndex}`;
        screenDescriptions[screenName] = makeClassScreen(table, true, components);

        tabs.push({
            screenName: makeScreenProperty(screenName),
            title: sheetNameForTable(table),
            hidden: false,
            icon: getRandomIcon(false),
        });

        freeScreenIndex++;
    }

    for (const table of tables) {
        const tableName = getTableName(table);

        screenDescriptions[classScreenName(tableName)] = makeDefaultClassScreenForPage(table, false);
        screenDescriptions[addClassScreenName(tableName)] = makeAddOrEditScreenDescriptionForTable(table, false);
        screenDescriptions[editClassScreenName(tableName)] = makeAddOrEditScreenDescriptionForTable(table, true);
    }

    return {
        ...makeSourceMetadataForAppDescription(sourceMetadataArray),
        title: settings.title,
        author: settings.author,
        description: settings.description ?? defaultAppDescriptionText,
        iconImage: settings.iconImage,
        tabs,
        screenDescriptions,
        theme: settings.theme,
        authentication: settings.authentication,
        features: definedMap(features, isolateNonUserAppFeatures) ?? defaultNonUserAppFeatures,
        userProfile: undefined,
    };
}
